import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import RichEditor from "@/components/RichEditor";
import { api } from "@/lib/api";
import { CATEGORIAS, extractVariables, prettyLabel, fillTemplate, autoFillFromTrabajador } from "@/lib/acciones";
import { generarAccionPDF } from "@/lib/accionesPDF";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import {
  Gavel, Plus, Search, Pencil, Trash2, ArrowLeft, FileOutput, Save, Variable, EyeOff, Eye, User,
  FolderArchive, Download, FileText, Building2, Upload,
} from "lucide-react";

// ============================================================================
// LIST VIEW (templates grid; header rendered by root)
// ============================================================================
function PlantillasListInner({ templates, onEdit, onDelete, onGenerar }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return templates;
    return templates.filter(t => (t.nombre || "").toLowerCase().includes(s) || (t.categoria || "").toLowerCase().includes(s));
  }, [templates, q]);

  return (
    <>
      <div className="np-card p-3 mb-5">
        <div className="flex items-center gap-2 px-2">
          <Search size={16} className="text-secondary-c" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar plantillas..."
            className="flex-1 bg-transparent outline-none text-sm py-2" data-testid="acc-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="np-card p-14 text-center text-secondary-c">
          {templates.length === 0 ? "Aún no tienes plantillas. Crea la primera." : "Sin resultados."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => {
            const vars = extractVariables(t.contenido_html || "");
            return (
              <div key={t.id} className="np-card p-5 flex flex-col gap-4" data-testid={`acc-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-xl font-semibold leading-tight">{t.nombre}</h3>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#FAFAF7] text-secondary-c shrink-0">{t.categoria || "Otro"}</span>
                </div>
                {t.descripcion && <p className="text-sm text-secondary-c line-clamp-3">{t.descripcion}</p>}
                <div className="flex items-center gap-3 text-xs text-secondary-c mt-auto">
                  <span className="flex items-center gap-1"><Variable size={13} /> {vars.length} campos</span>
                  <span>{new Date(t.created_at).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onGenerar(t)} className="np-btn np-btn-primary flex-1 justify-center text-sm" data-testid={`acc-generar-${t.id}`}>
                    <FileOutput size={14} className="mr-1" /> Generar
                  </button>
                  <button onClick={() => onEdit(t)} className="np-btn np-btn-outline p-2" title="Editar" data-testid={`acc-edit-${t.id}`}><Pencil size={14} /></button>
                  <button onClick={() => onDelete(t)} className="np-btn np-btn-outline p-2 text-error hover:!bg-[#FDEDED]" title="Eliminar" data-testid={`acc-delete-${t.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ============================================================================
// EDITOR (create / edit template)
// ============================================================================
function PlantillaEditor({ initial, onBack, onSaved }) {
  const [nombre, setNombre] = useState(initial?.nombre || "");
  const [categoria, setCategoria] = useState(initial?.categoria || "Otro");
  const [descripcion, setDescripcion] = useState(initial?.descripcion || "");
  const [contenido, setContenido] = useState(initial?.contenido_html || "");
  const [incluirEncabezado, setIncluirEncabezado] = useState(initial?.incluir_encabezado_empresa !== false);
  const [saving, setSaving] = useState(false);
  const variables = useMemo(() => extractVariables(contenido), [contenido]);
  const isEdit = !!initial?.id;

  const guardar = async () => {
    if (!nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!contenido || contenido === "<p></p>") { toast.error("El contenido es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        categoria,
        descripcion: descripcion.trim(),
        contenido_html: contenido,
        incluir_encabezado_empresa: incluirEncabezado,
      };
      const r = isEdit ? await api.put(`/acciones-templates/${initial.id}`, payload) : await api.post("/acciones-templates", payload);
      toast.success(isEdit ? "Plantilla actualizada" : "Plantilla creada");
      onSaved?.(r.data);
    } catch (e) {
      toast.error("No se pudo guardar la plantilla");
    } finally { setSaving(false); }
  };

  return (
    <>
      <header className="flex items-center gap-3 mb-6 fade-in-up">
        <button onClick={onBack} className="np-btn np-btn-ghost p-2" data-testid="acc-back-list"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-3xl font-semibold leading-tight">{isEdit ? "Editar Plantilla" : "Nueva Plantilla"}</h1>
          <p className="text-sm text-secondary-c">
            Usa <code className="px-1.5 py-0.5 rounded bg-[#FAFAF7] text-xs">{"{{nombre_campo}}"}</code> para crear campos variables.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
        <div className="np-card p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1.5">Nombre *</label>
              <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Contrato de arrendamiento"
                className="np-input w-full" data-testid="acc-form-nombre" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Categoría</label>
              <select value={categoria} onChange={e => setCategoria(e.target.value)} className="np-input w-full" data-testid="acc-form-categoria">
                {CATEGORIAS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1.5">Descripción</label>
            <input value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Descripción breve de la plantilla..."
              className="np-input w-full" data-testid="acc-form-descripcion" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1.5">Contenido de la Plantilla *</label>
            <RichEditor value={contenido} onChange={setContenido} testid="acc-form-editor"
              placeholder="Escribe el contenido. Usa {{nombre_campo}} para crear campos variables..." />
          </div>
        </div>

        <div className="space-y-4">
          <div className="np-card p-5">
            <h3 className="font-display text-base font-semibold mb-2 flex items-center gap-2"><Building2 size={16} /> Encabezado del PDF</h3>
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={incluirEncabezado}
                onChange={e => setIncluirEncabezado(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#274735]"
                data-testid="acc-form-encabezado"
              />
              <span className="text-sm leading-tight">
                Incluir <strong>logo</strong> y <strong>nombre</strong> de la empresa al inicio del documento.
                <span className="block text-xs text-secondary-c mt-0.5">
                  Desactivar para documentos que entrega el trabajador (ej. carta de renuncia).
                </span>
              </span>
            </label>
          </div>
          <div className="np-card p-5">
            <h3 className="font-display text-base font-semibold mb-2 flex items-center gap-2"><Variable size={16} /> Campos Variables</h3>
            <p className="text-xs text-secondary-c italic mb-3">
              Escribe <code className="px-1 py-0.5 rounded bg-[#FAFAF7]">{"{{nombre_campo}}"}</code> en tu plantilla para crear campos variables.
            </p>
            {variables.length === 0 ? (
              <div className="text-xs text-secondary-c py-3">Aún no detectamos ningún campo.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="acc-vars-preview">
                {variables.map(v => (
                  <span key={v} className="text-[11px] px-2 py-1 rounded-full bg-[#E6EFEA] text-primary-c font-medium">{prettyLabel(v)}</span>
                ))}
              </div>
            )}
          </div>
          <button onClick={guardar} disabled={saving} className="np-btn np-btn-primary w-full justify-center" data-testid="acc-form-save">
            <Save size={16} className="mr-1.5" /> {saving ? "Guardando..." : (isEdit ? "Guardar Cambios" : "Crear Plantilla")}
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// GENERATE DOCUMENT
// ============================================================================
function GenerarDocumento({ template, empleados, empresa, onBack, onSaved }) {
  const variables = useMemo(() => extractVariables(template.contenido_html || ""), [template]);
  const includeHeader = template.incluir_encabezado_empresa !== false;
  const [docName, setDocName] = useState(`${template.nombre} - ${new Date().toLocaleDateString("es-CR")}`);
  const [empleadoId, setEmpleadoId] = useState("");
  const [values, setValues] = useState(() => Object.fromEntries(variables.map(v => [v, ""])));
  const [showPreview, setShowPreview] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [adjunto, setAdjunto] = useState(null); // { fileName, base64 }

  const onPickAdjunto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo se admiten archivos PDF"); e.target.value = ""; return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo no debe superar los 10 MB"); e.target.value = ""; return;
    }
    const buf = await file.arrayBuffer();
    const b64 = btoa(new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""));
    setAdjunto({ fileName: file.name, base64: b64 });
    toast.success(`Documento adjuntado: ${file.name}`);
  };

  const onPickEmpleado = (eid) => {
    setEmpleadoId(eid);
    if (!eid) return;
    const emp = empleados.find(e => e.id === eid);
    if (!emp) return;
    const filled = autoFillFromTrabajador(variables, emp, empresa);
    setValues(v => ({ ...v, ...filled }));
    toast.success(`Campos auto-rellenados desde ${emp.nombre}`);
  };

  const filledHtml = useMemo(() => fillTemplate(template.contenido_html || "", values), [template, values]);

  const generar = async () => {
    setGenerating(true);
    try {
      const safe = docName.replace(/[^A-Za-z0-9_\-]+/g, "_");
      const result = await generarAccionPDF({
        empresa, html: filledHtml, fileName: `${safe}.pdf`,
        docTitle: template.categoria || "",
        includeEmpresaHeader: includeHeader,
      });
      // Save to backend
      await api.post("/acciones-documentos", {
        template_id: template.id,
        template_nombre: template.nombre,
        categoria: template.categoria,
        empleado_id: empleadoId || null,
        nombre: docName,
        contenido_html: filledHtml,
        values,
        file_name: result.fileName,
        pdf_base64: result.pdfBase64,
        fecha_emision: new Date().toISOString().slice(0, 10),
        adjunto_pdf_base64: adjunto?.base64 || null,
        adjunto_file_name: adjunto?.fileName || null,
      });
      toast.success("Documento generado y archivado");
      onSaved?.();
    } catch (e) {
      console.error(e);
      toast.error("No se pudo generar el documento");
    } finally { setGenerating(false); }
  };

  return (
    <>
      <header className="flex items-center gap-3 mb-6 fade-in-up">
        <button onClick={onBack} className="np-btn np-btn-ghost p-2" data-testid="acc-gen-back"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-3xl font-semibold leading-tight">Generar Documento</h1>
          <p className="text-sm text-secondary-c">Basado en: <span className="font-medium text-primary-c">{template.nombre}</span></p>
        </div>
      </header>

      <div className={`grid grid-cols-1 ${showPreview ? "lg:grid-cols-2" : ""} gap-5`}>
        <div className="space-y-4">
          <div className="np-card p-5">
            <label className="text-sm font-medium block mb-1.5">Nombre del Documento</label>
            <input value={docName} onChange={e => setDocName(e.target.value)} className="np-input w-full" data-testid="acc-gen-name" />
          </div>

          <div className="np-card p-5">
            <label className="text-sm font-medium flex items-center gap-2 mb-1.5"><User size={15} /> Rellenar desde Trabajador</label>
            <select value={empleadoId} onChange={e => onPickEmpleado(e.target.value)} className="np-input w-full" data-testid="acc-gen-empleado">
              <option value="">Selecciona un trabajador...</option>
              {empleados.filter(e => e.estado === "Activo").map(e => (
                <option key={e.id} value={e.id}>{e.nombre} {e.apellidos} — {e.cedula}</option>
              ))}
            </select>
            <p className="text-xs text-secondary-c mt-1.5">Los campos coincidentes se rellenarán automáticamente.</p>
          </div>

          {variables.length > 0 && (
            <div className="np-card p-5 space-y-3">
              <div className="text-sm font-semibold uppercase tracking-widest text-secondary-c">Completa los Campos</div>
              {variables.map(v => (
                <div key={v}>
                  <label className="text-sm font-medium block mb-1">{prettyLabel(v)}</label>
                  <input
                    value={values[v] || ""}
                    onChange={e => setValues(s => ({ ...s, [v]: e.target.value }))}
                    placeholder={`Ingresa ${prettyLabel(v).toLowerCase()}...`}
                    className="np-input w-full"
                    data-testid={`acc-gen-field-${v}`} />
                </div>
              ))}
            </div>
          )}

          <div className="np-card p-5">
            <label className="text-sm font-medium flex items-center gap-2 mb-1.5"><Upload size={15} /> Documento de respaldo (opcional)</label>
            <p className="text-xs text-secondary-c mb-3">Adjuntá un PDF (constancia médica, autorización firmada, etc.). Se guarda con el expediente del trabajador. Máx. 10 MB.</p>
            {adjunto ? (
              <div className="flex items-center justify-between gap-3 p-3 bg-[#E6EFEA] rounded-xl">
                <div className="flex items-center gap-2 text-sm min-w-0">
                  <FileText size={16} className="text-primary-c flex-shrink-0" />
                  <span className="truncate" data-testid="acc-gen-adjunto-name">{adjunto.fileName}</span>
                </div>
                <button onClick={() => setAdjunto(null)} className="np-btn np-btn-ghost text-xs" data-testid="acc-gen-adjunto-remove">Quitar</button>
              </div>
            ) : (
              <label className="np-btn np-btn-outline w-full justify-center cursor-pointer" data-testid="acc-gen-adjunto-label">
                <Upload size={14} className="mr-1.5" /> Elegir PDF
                <input type="file" accept="application/pdf,.pdf" onChange={onPickAdjunto} className="hidden" data-testid="acc-gen-adjunto-input" />
              </label>
            )}
          </div>

          <button onClick={generar} disabled={generating} className="np-btn np-btn-primary w-full justify-center" data-testid="acc-gen-submit">
            <FileOutput size={16} className="mr-1.5" /> {generating ? "Generando..." : "Generar y Guardar Documento"}
          </button>
        </div>

        {showPreview && (
          <div className="lg:sticky lg:top-[180px] self-start">
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Vista Previa</div>
              <button onClick={() => setShowPreview(false)} className="text-xs text-secondary-c hover:text-primary-c flex items-center gap-1" data-testid="acc-toggle-preview">
                <EyeOff size={13} /> Ocultar
              </button>
            </div>
            <div className="np-card p-6 max-h-[70vh] overflow-y-auto">
              {includeHeader && (
                <div className="text-center pb-3 mb-4 border-b border-c">
                  <div className="font-display text-lg font-semibold">{empresa?.razon_social || empresa?.nombre || "Empresa"}</div>
                </div>
              )}
              <div className="tiptap-content prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(filledHtml) }} />
            </div>
          </div>
        )}
        {!showPreview && (
          <button onClick={() => setShowPreview(true)} className="np-btn np-btn-outline lg:absolute lg:right-10 lg:top-[180px] gap-1 text-xs" data-testid="acc-toggle-preview-show">
            <Eye size={13} /> Ver vista previa
          </button>
        )}
      </div>
    </>
  );
}

// ============================================================================
// DOCUMENTOS GENERADOS LIST
// ============================================================================
function DocumentosList({ empleados, reloadKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const empMap = useMemo(() => {
    const m = {};
    empleados.forEach(e => { m[e.id] = `${e.nombre} ${e.apellidos || ""}`.trim(); });
    return m;
  }, [empleados]);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/acciones-documentos");
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [reloadKey]);

  const descargar = async (id) => {
    try {
      const { data } = await api.get(`/acciones-documentos/${id}`);
      if (!data?.pdf_base64) { toast.error("Documento sin PDF almacenado"); return; }
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${data.pdf_base64}`;
      link.download = data.file_name || `${data.nombre || "documento"}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error("No se pudo descargar el documento"); }
  };

  const eliminar = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/acciones-documentos/${confirmDel.id}`);
      toast.success("Documento eliminado");
      setConfirmDel(null);
      await reload();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it =>
      (it.nombre || "").toLowerCase().includes(s) ||
      (it.template_nombre || "").toLowerCase().includes(s) ||
      (empMap[it.empleado_id] || "").toLowerCase().includes(s)
    );
  }, [items, q, empMap]);

  return (
    <>
      <div className="np-card p-3 mb-5">
        <div className="flex items-center gap-2 px-2">
          <Search size={16} className="text-secondary-c" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, plantilla o trabajador..."
            className="flex-1 bg-transparent outline-none text-sm py-2" data-testid="acc-docs-search" />
          {items.length > 0 && <span className="text-xs text-secondary-c whitespace-nowrap" data-testid="acc-docs-count">{filtered.length} de {items.length}</span>}
        </div>
      </div>

      {loading ? (
        <div className="np-card p-10 text-center text-secondary-c">Cargando documentos…</div>
      ) : items.length === 0 ? (
        <div className="np-card p-14 text-center text-secondary-c">
          <FolderArchive size={36} className="mx-auto mb-3 opacity-60" />
          Aún no has generado ningún documento.
        </div>
      ) : filtered.length === 0 ? (
        <div className="np-card p-10 text-center text-secondary-c">Sin resultados para “{q}”.</div>
      ) : (
        <div className="np-card overflow-hidden" data-testid="acc-docs-list">
          <table className="np-table">
            <thead>
              <tr><th>Documento</th><th>Plantilla</th><th>Trabajador</th><th>Fecha</th><th className="text-right">Acciones</th></tr>
            </thead>
            <tbody>
              {filtered.map(it => (
                <tr key={it.id} data-testid={`acc-doc-row-${it.id}`}>
                  <td>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
                        <FileText size={14} strokeWidth={1.5} />
                      </div>
                      <div className="font-medium">{it.nombre || it.file_name}</div>
                    </div>
                  </td>
                  <td><span className="text-secondary-c">{it.template_nombre || "—"}</span></td>
                  <td>{it.empleado_id ? (empMap[it.empleado_id] || <span className="text-secondary-c italic">Empleado eliminado</span>) : <span className="text-secondary-c italic">Sin trabajador</span>}</td>
                  <td className="text-secondary-c text-xs">{new Date(it.created_at).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => descargar(it.id)} className="np-btn np-btn-outline p-2" title="Descargar PDF" data-testid={`acc-doc-download-${it.id}`}><Download size={14} /></button>
                      <button onClick={() => setConfirmDel(it)} className="np-btn np-btn-ghost p-2 text-error hover:bg-[#FDEDED]" title="Eliminar" data-testid={`acc-doc-delete-${it.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="np-card p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-2">Eliminar documento</h3>
            <p className="text-sm text-secondary-c mb-4">¿Seguro que querés eliminar <strong>{confirmDel.nombre}</strong>?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="np-btn np-btn-outline" data-testid="acc-doc-confirm-cancel">Cancelar</button>
              <button onClick={eliminar} className="np-btn np-btn-primary !bg-error" data-testid="acc-doc-confirm-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// ROOT (state machine: list | edit | generate, with tabs)
// ============================================================================
export default function AccionesPersonal() {
  const [mode, setMode] = useState("list"); // list | edit | generate
  const [section, setSection] = useState("plantillas"); // plantillas | documentos
  const [templates, setTemplates] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [current, setCurrent] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [docsReloadKey, setDocsReloadKey] = useState(0);

  const reload = async () => {
    const r = await api.get("/acciones-templates");
    setTemplates(r.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        const [t, e, c] = await Promise.all([
          api.get("/acciones-templates"),
          api.get("/empleados"),
          api.get("/empresa"),
        ]);
        setTemplates(t.data || []);
        setEmpleados(e.data || []);
        setEmpresa(c.data || {});
      } catch (err) { /* ignore */ }
    })();
  }, []);

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/acciones-templates/${confirmDel.id}`);
      toast.success("Plantilla eliminada");
      setConfirmDel(null);
      await reload();
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <Layout>
      {mode === "list" && (
        <>
          <header className="flex items-end justify-between mb-6 flex-wrap gap-4 fade-in-up">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><Gavel size={26} strokeWidth={1.5} /></div>
              <div>
                <h1 className="font-display text-3xl font-semibold">Documentación</h1>
                <p className="text-sm text-secondary-c">Plantillas reutilizables y archivo de documentos generados.</p>
              </div>
            </div>
            {section === "plantillas" && (
              <button onClick={() => { setCurrent(null); setMode("edit"); }} className="np-btn np-btn-primary" data-testid="acc-new-template">
                <Plus size={16} className="mr-1.5" /> Nueva Plantilla
              </button>
            )}
          </header>

          <div className="inline-flex p-1 rounded-xl bg-[#FAFAF7] border border-c mb-5">
            <button
              onClick={() => setSection("plantillas")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${section === "plantillas" ? "bg-white shadow-sm text-primary-c" : "text-secondary-c hover:text-primary-c"}`}
              data-testid="acc-tab-plantillas"
            >
              <Gavel size={14} /> Plantillas
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E6EFEA] text-primary-c">{templates.length}</span>
            </button>
            <button
              onClick={() => { setSection("documentos"); setDocsReloadKey(k => k + 1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${section === "documentos" ? "bg-white shadow-sm text-primary-c" : "text-secondary-c hover:text-primary-c"}`}
              data-testid="acc-tab-documentos"
            >
              <FolderArchive size={14} /> Documentos Generados
            </button>
          </div>

          {section === "plantillas" ? (
            <PlantillasListInner
              templates={templates}
              onEdit={(t) => { setCurrent(t); setMode("edit"); }}
              onGenerar={(t) => { setCurrent(t); setMode("generate"); }}
              onDelete={(t) => setConfirmDel(t)}
            />
          ) : (
            <DocumentosList empleados={empleados} reloadKey={docsReloadKey} />
          )}
        </>
      )}
      {mode === "edit" && (
        <PlantillaEditor initial={current} onBack={() => setMode("list")} onSaved={async () => { await reload(); setMode("list"); }} />
      )}
      {mode === "generate" && current && (
        <GenerarDocumento template={current} empleados={empleados} empresa={empresa}
          onBack={() => setMode("list")} onSaved={() => { setDocsReloadKey(k => k + 1); setSection("documentos"); setMode("list"); }} />
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="np-card p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-2">Eliminar plantilla</h3>
            <p className="text-sm text-secondary-c mb-4">¿Seguro que querés eliminar <strong>{confirmDel.nombre}</strong>? Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="np-btn np-btn-outline" data-testid="acc-confirm-cancel">Cancelar</button>
              <button onClick={doDelete} className="np-btn np-btn-primary !bg-error" data-testid="acc-confirm-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
